export type UnitFamily = "weight" | "volume" | "count";

export interface Quantity {
  amount: number;
  unit: string;
  family: UnitFamily;
}

export const UNIT_PATTERN = /(\d+(?:[.,]\d+)?)\s*(kg|g|ml|l|un|pct|lt)\b/gi;

const UNIT_FAMILY: Record<string, { family: UnitFamily; base: string; factor: number }> = {
  kg: { family: "weight", base: "kg", factor: 1 },
  g: { family: "weight", base: "kg", factor: 0.001 },
  l: { family: "volume", base: "l", factor: 1 },
  lt: { family: "volume", base: "l", factor: 1 },
  ml: { family: "volume", base: "l", factor: 0.001 },
  un: { family: "count", base: "un", factor: 1 },
  pct: { family: "count", base: "un", factor: 1 },
};

export function extractQuantity(name: string): Quantity | null {
  const re = new RegExp(UNIT_PATTERN.source, "gi");
  const match = re.exec(name);
  if (!match) return null;

  const raw = match[1].replace(",", ".");
  const amount = parseFloat(raw);
  const unitKey = match[2].toLowerCase();
  const info = UNIT_FAMILY[unitKey];

  return {
    amount: Math.round(amount * info.factor * 1000) / 1000,
    unit: info.base,
    family: info.family,
  };
}

export interface UnitPrice {
  value: number;
  unit: string;
}

export function computeUnitPrice(
  price: number,
  quantity: Quantity | null
): UnitPrice | null {
  if (!quantity || quantity.amount === 0) return null;
  const value = Math.round((price / quantity.amount) * 100) / 100;
  return { value, unit: quantity.unit };
}

export function areUnitsCompatible(
  a: { family: string } | null,
  b: { family: string } | null
): boolean {
  if (!a || !b) return false;
  return a.family === b.family;
}

export type UnitPriceRank = "best" | "mid" | "worst";

export function rankUnitPrice(
  prices: ({ value: number } | null)[]
): (UnitPriceRank | null)[] {
  const valid = prices
    .map((p, i) => (p ? { value: p.value, index: i } : null))
    .filter((x): x is { value: number; index: number } => x !== null);

  if (valid.length === 0) return prices.map(() => null);

  const sorted = [...valid].sort((a, b) => a.value - b.value);
  const minVal = sorted[0].value;
  const maxVal = sorted[sorted.length - 1].value;

  return prices.map((p) => {
    if (!p) return null;
    if (minVal === maxVal) return "best";
    if (p.value === minVal) return "best";
    if (p.value === maxVal) return "worst";
    return "mid";
  });
}
